import React from 'react';

/**
 * Étape 6 du formulaire TICFE : récapitulatif final, vérification d'éligibilité,
 * sélection des sites à inclure (multi) et déclenchement de la génération du rapport.
 */
export const Step6Validation = ({ ctx }) => {
  const {
    formData, setFormData, rapportType, setStep,
    consommationsParPDL, nafEligMapTicfe,
    handleGenerateRapport,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2">Validation avant génération</h2>
      <p className="text-sm text-[#687175] mb-6">
        Vérifiez les informations et sélectionnez les sites à inclure dans le calcul des économies.
      </p>

      {/* Récapitulatif entreprise */}
      <div className="bg-[#F5F6F9] rounded-lg p-4 mb-6">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div><span className="text-[#687175]">Entreprise :</span><div className="font-semibold text-[#191D1E]">{formData.nom_entreprise}</div></div>
          <div><span className="text-[#687175]">SIREN :</span><div className="font-mono font-semibold text-[#191D1E]">{formData.siren}</div></div>
          <div><span className="text-[#687175]">Période :</span><div className="text-[#191D1E]">{formData.debut_periode} → {formData.fin_periode}</div></div>
        </div>
        <div className="mt-3 flex gap-3">
          <button onClick={() => setStep(3)} className="text-xs text-[#397DFF] hover:underline" data-testid="edit-company-info">Modifier les informations entreprise →</button>
          <button onClick={() => setStep(rapportType === 'mono' ? 3 : 4)} className="text-xs text-[#397DFF] hover:underline" data-testid="edit-sites-pdl">Modifier les sites / PDL →</button>
          <button onClick={() => setStep(5)} className="text-xs text-[#397DFF] hover:underline" data-testid="edit-consumptions">Modifier les consommations →</button>
        </div>
      </div>

      {/* Vérification des calculs d'éligibilité */}
      {(() => {
        const vac = parseFloat(formData.valeur_ajoutee_siren) || 0;
        const tarifCerfa = 22.5;
        let totalConsoKwh = 0;
        const allPdls = rapportType === 'multi'
          ? formData.sites.flatMap(s => (s.pdls || []).map(p => ({ ...p, excluded: s.taux_reduit_deja_applique })))
          : (formData.pdls || []).map(p => ({ ...p, excluded: false }));
        allPdls.forEach(pdl => {
          const consos = consommationsParPDL[pdl.pdl] || {};
          Object.values(consos).forEach(v => { totalConsoKwh += parseFloat(v) || 0; });
        });
        const totalConsoMwh = totalConsoKwh / 1000;
        const ticfeCerfa = totalConsoMwh * tarifCerfa;
        const ratio = vac > 0 ? (ticfeCerfa / vac) : 0;
        const ratioPercent = (ratio * 100).toFixed(3);
        const ratioOk = ratio >= 0.005;

        const pdlStatuses = allPdls.map(pdl => {
          const code = (pdl.code_naf || '').trim().toUpperCase();
          const etype = nafEligMapTicfe[code];
          const nafLoaded = code in nafEligMapTicfe;
          const nafQualifie = etype === 'EXONERATION' || etype === 'TARIF_REDUIT'
            || etype === 'TARIF_REDUIT_AVEC_RATIO' || etype === 'TARIF_REDUIT_SANS_RATIO';
          const isExo = etype === 'EXONERATION';
          const eligible = nafQualifie && (isExo || ratioOk);
          return { pdl: pdl.pdl, code, etype, nafLoaded, nafQualifie, isExo, eligible };
        });
        const anyEligible = pdlStatuses.some(s => s.eligible);
        const anyNafQualifie = pdlStatuses.some(s => s.nafQualifie);
        const anyLoading = pdlStatuses.some(s => !s.nafLoaded);
        const nafBlocant = !anyNafQualifie && !anyLoading && pdlStatuses.length > 0;
        const isEligiblePreview = anyEligible;
        const showLoading = anyLoading;
        const formatEur = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

        return (
          <div className={`border-2 rounded-lg p-5 mb-6 ${isEligiblePreview ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`} data-testid="validation-calculs">
            <h3 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-[#687175]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              Calcul d'éligibilité (vérification)
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-[#687175]">Valeur Ajoutée Comptable (VAC)</span>
                <span className="font-mono font-semibold text-[#191D1E]" data-testid="validation-vac">{formatEur(vac)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-[#687175]">Consommation totale</span>
                <span className="font-mono font-semibold text-[#191D1E]" data-testid="validation-conso">{totalConsoMwh.toFixed(2)} MWh</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-[#687175]">TICFE CERFA officiel ({tarifCerfa} €/MWh)</span>
                <span className="font-mono font-semibold text-blue-700" data-testid="validation-ticfe-cerfa">{formatEur(ticfeCerfa)}</span>
              </div>
              <div className={`flex justify-between items-center py-2 border-b-2 ${ratioOk ? 'border-emerald-400' : 'border-red-400'}`}>
                <span className="text-sm font-medium text-[#191D1E]">Ratio TICFE CERFA / VAC</span>
                <span className={`font-mono font-bold text-lg ${ratioOk ? 'text-emerald-600' : 'text-red-500'}`} data-testid="validation-ratio">
                  {ratioPercent.replace('.', ',')} %
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="text-xs font-semibold text-[#687175] uppercase mb-2">Éligibilité NAF par PDL</div>
              <div className="space-y-1.5">
                {pdlStatuses.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1" data-testid={`pdl-naf-status-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-[#191D1E]">{s.pdl}</span>
                      <span className="text-xs text-[#687175]">{s.code || <span className="italic">Code NAF manquant</span>}</span>
                    </div>
                    <div>
                      {!s.nafLoaded ? (
                        <span className="text-xs text-[#687175] italic">Chargement…</span>
                      ) : s.nafQualifie ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                          {s.etype === 'EXONERATION' ? 'Exonération' : 'Tarif réduit possible'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                          Non qualifiant ({s.etype || 'aucune règle'})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`mt-4 flex items-start gap-3 p-3 rounded-lg ${isEligiblePreview ? 'bg-emerald-100' : 'bg-red-100'}`} data-testid="validation-verdict">
              {isEligiblePreview ? (
                <>
                  <svg className="w-6 h-6 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <div>
                    <span className="font-semibold text-emerald-800">ÉLIGIBLE</span>
                    <span className="text-sm text-emerald-700 ml-2">
                      {showLoading ? 'Vérification NAF en cours…' : `Au moins un PDL qualifie (ratio ${ratioPercent.replace('.', ',')}%)`}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <div>
                    <span className="font-semibold text-red-800">NON ÉLIGIBLE</span>
                    <span className="text-sm text-red-700 ml-2 block">
                      {showLoading ? 'Vérification NAF en cours…' :
                        nafBlocant ? 'Aucun PDL n\'a de code NAF qualifiant (Exonération ou Tarif réduit).' :
                        !ratioOk ? `Ratio (${ratioPercent.replace('.', ',')}%) inférieur au seuil 0,500%.` :
                        'Conditions d\'éligibilité non réunies.'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {rapportType === 'multi' && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[#191D1E] mb-4">Sites à inclure dans le calcul</h3>
          <div className="space-y-3">
            {formData.sites.map((site, siteIdx) => {
              const sitePDLs = site.pdls || [];
              const totalConso = sitePDLs.reduce((sum, pdl) => {
                const consos = consommationsParPDL[pdl.pdl] || {};
                return sum + Object.values(consos).reduce((s, v) => s + v, 0);
              }, 0);
              const isExcluded = site.taux_reduit_deja_applique;

              return (
                <div key={siteIdx} className={`border rounded-lg overflow-hidden transition-all ${isExcluded ? 'border-blue-300 bg-blue-50/50 opacity-75' : 'border-[#BFC6CC]'}`}
                  data-testid={`validation-site-${siteIdx}`}>
                  <div className="flex items-center gap-4 px-5 py-4">
                    <label className="flex items-center gap-3 cursor-pointer flex-1">
                      <input type="checkbox" checked={!isExcluded}
                        onChange={(e) => {
                          const newSites = [...formData.sites];
                          newSites[siteIdx] = { ...newSites[siteIdx], taux_reduit_deja_applique: !e.target.checked };
                          setFormData({ ...formData, sites: newSites });
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-[#397DFF] focus:ring-[#397DFF]"
                        data-testid={`validation-site-checkbox-${siteIdx}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-[#191D1E]">{site.nom_site || `Site ${siteIdx + 1}`}</span>
                          <span className="font-mono text-sm text-[#687175]">SIRET: {site.siret}</span>
                          {isExcluded && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Taux réduit déjà appliqué</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#687175] mt-1">
                          <span>{sitePDLs.length} PDL</span>
                          <span>Conso. totale : {(totalConso / 1000).toFixed(1)} MWh</span>
                        </div>
                      </div>
                    </label>
                  </div>

                  {!isExcluded && sitePDLs.some(p => p.tranches_eligibilite && p.tranches_eligibilite.length > 0) && (
                    <div className="border-t border-[#BFC6CC] px-5 py-3 bg-blue-50">
                      <div className="text-xs font-semibold text-blue-700 mb-2">Tranches d'éligibilité :</div>
                      <div className="space-y-2">
                        {sitePDLs.filter(p => p.tranches_eligibilite && p.tranches_eligibilite.length > 0).map((pdl, pidx) => (
                          <div key={pidx} className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-[#687175]">{pdl.pdl}</span>
                            <div className="flex flex-wrap gap-1">
                              {pdl.tranches_eligibilite.map((t, ti) => (
                                <span key={ti} className={`inline-block text-xs px-1.5 py-0.5 rounded ${t.motif === 'EXONERATION' ? 'bg-emerald-100 text-emerald-700' : t.motif === 'FUITE_CARBONE' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {t.part}% {t.motif === 'EXONERATION' ? 'exo' : t.motif === 'FUITE_CARBONE' ? 'fuite C' : 'élec-int'}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isExcluded && (
                    <div className="border-t border-[#BFC6CC] px-5 py-3">
                      <div className="space-y-2">
                        {sitePDLs.map((pdl, pidx) => {
                          const pdlConso = Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((s, v) => s + v, 0);
                          return (
                            <div key={pidx} className="flex items-center gap-3 text-sm flex-wrap">
                              <span className="font-mono text-[#191D1E]">{pdl.pdl}</span>
                              <span className="text-[#687175]">{pdl.puissance_souscrite} kVA</span>
                              <span className="text-[#687175]">{pdl.code_naf}</span>
                              <span className="text-[#687175]">{(pdlConso / 1000).toFixed(1)} MWh</span>
                              {pdl.tranches_eligibilite && pdl.tranches_eligibilite.length > 0 && (
                                <div className="ml-auto flex flex-wrap gap-1">
                                  {pdl.tranches_eligibilite.map((t, ti) => (
                                    <span key={ti} className={`inline-block text-xs px-1.5 py-0.5 rounded ${t.motif === 'EXONERATION' ? 'bg-emerald-100 text-emerald-700' : t.motif === 'FUITE_CARBONE' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {t.part}% {t.motif === 'EXONERATION' ? 'exo' : t.motif === 'FUITE_CARBONE' ? 'fuite C' : 'élec-int'}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-4 bg-[#10D183]/10 border border-[#10D183]/30 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#191D1E] font-medium">
                Sites inclus dans le calcul : {formData.sites.filter(s => !s.taux_reduit_deja_applique).length} / {formData.sites.length}
              </span>
              <span className="text-[#687175]">
                Sites exclus (taux réduit déjà appliqué) : {formData.sites.filter(s => s.taux_reduit_deja_applique).length}
              </span>
            </div>
          </div>
        </div>
      )}

      {rapportType === 'mono' && formData.pdls.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[#191D1E] mb-4">PDL à analyser</h3>
          <div className="space-y-3">
            {formData.pdls.map((pdl, idx) => {
              const pdlConso = Object.values(consommationsParPDL[pdl.pdl] || {}).reduce((s, v) => s + v, 0);
              return (
                <div key={idx} className="p-4 border border-[#BFC6CC] rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</span>
                    <span className="text-sm text-[#687175]">{pdl.puissance_souscrite} kVA</span>
                    <span className="text-sm text-[#687175]">{pdl.code_naf}</span>
                    <span className="text-sm text-[#687175]">{(pdlConso / 1000).toFixed(1)} MWh</span>
                  </div>
                  {pdl.tranches_eligibilite && pdl.tranches_eligibilite.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {pdl.tranches_eligibilite.map((t, ti) => (
                        <span key={ti} className={`inline-block text-xs px-1.5 py-0.5 rounded ${t.motif === 'EXONERATION' ? 'bg-emerald-100 text-emerald-700' : t.motif === 'FUITE_CARBONE' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                          {t.part}% {t.motif === 'EXONERATION' ? 'exo' : t.motif === 'FUITE_CARBONE' ? 'fuite C' : 'élec-int'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-between gap-3 pt-6 border-t border-[#BFC6CC]">
        <button onClick={() => setStep(5)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]">← Retour</button>
        <button onClick={() => handleGenerateRapport()} className="bg-[#10D183] hover:bg-[#0BB873] text-white font-semibold py-3 px-10 rounded-lg text-lg shadow-lg" data-testid="generate-rapport-btn">
          Générer le Rapport Digital
        </button>
      </div>
    </div>
  );
};

export default Step6Validation;
